import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useFirebaseAuthentication } from 'lib/firebase';
import { db, functions } from 'lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import SiteWrapper from '../../home-pages/SiteWrapper';

const MomAnswerPage = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!questionId) return;

    const fetchQuestion = async () => {
      try {
        const questionDoc = await getDoc(doc(db, 'weekly_questions', questionId));
        
        if (questionDoc.exists()) {
          const data = questionDoc.data();
          setQuestion({ id: questionDoc.id, ...data });
          
          // Pre-fill answer if already answered
          if (data.momAnswer) {
            setAnswer(data.momAnswer);
            setSubmitted(true);
          }
        } else {
          setSnackbar({
            open: true,
            message: 'Question not found',
            severity: 'error',
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to load question',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId]);

  const handleSubmit = async () => {
    if (!answer.trim()) {
      setSnackbar({
        open: true,
        message: 'Please write your answer before submitting',
        severity: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      const submitAnswerFn = httpsCallable(functions, 'submit_answer');
      await submitAnswerFn({
        questionId,
        answerText: answer.trim(),
        photoUrls: [], // Future: add photo upload functionality
      });

      setSnackbar({
        open: true,
        message: 'Your answer has been saved! Thank you for sharing your story.',
        severity: 'success',
      });
      setSubmitted(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to submit answer',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: '#f5f5f5' }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Please Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You need to be signed in to answer questions.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/signin')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a3f8f 100%)',
              },
            }}
          >
            Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!question) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: '#f5f5f5' }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Question Not Found
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <SiteWrapper>
      <Helmet>
        <title>Answer Question - Mom Stories</title>
      </Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              ✨ Your Weekly Question ✨
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Take your time to reflect and share your thoughts
            </Typography>
          </Box>

          {/* Question Card */}
          <Card
            elevation={3}
            sx={{
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              border: '2px solid',
              borderColor: 'rgba(102, 126, 234, 0.2)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  color: '#333',
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                "{question.questionText}"
              </Typography>
              {question.publishDate && (
                <Typography variant="caption" color="text.secondary">
                  Week of {format(question.publishDate.toDate(), 'MMMM d, yyyy')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Answer Form */}
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            {submitted ? (
              <Box textAlign="center" py={4}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Thank You!
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Your answer has been saved. Your family will cherish this story.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/mom-stories')}
                  sx={{ mt: 2 }}
                >
                  View All Stories
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Your Answer
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  placeholder="Share your thoughts, memories, and stories here..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Georgia, serif',
                      fontSize: '16px',
                      lineHeight: 1.8,
                    },
                  }}
                />
                
                {/* Future: Photo upload section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    💡 Tip: Take your time and write as much or as little as you'd like. There's no rush!
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    onClick={handleSubmit}
                    disabled={submitting || !answer.trim()}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a3f8f 100%)',
                        px: 4,
                      },
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Answer'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </SiteWrapper>
  );
};

export default MomAnswerPage;
